import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ProviderCard } from '@/components/marketplace/ProviderCard';
import { ProviderSearchResult } from '@/hooks/useProviderSearch';
import { logger } from '@/lib/logger';

export function FeaturedProviders() {
  // Obtener proveedores destacados
  const { data: featuredProviders = [], isLoading } = useQuery({
    queryKey: ['featured-providers'],
    queryFn: async () => {
      logger.info('🔍 Fetching featured providers...');

      // Query base - Proveedores con servicios activos
      const { data: providers, error } = await supabase
        .from('profiles')
        .select(`
          id,
          business_name,
          bio,
          avatar_url,
          city,
          province,
          address,
          services!inner(id, name, category, price, is_active)
        `)
        .eq('user_type', 'provider')
        .eq('onboarding_completed', true)
        .eq('services.is_active', true)
        .limit(20); // Obtener 20 para luego filtrar los mejores 6

      if (error) throw error;
      if (!providers) return [];

      const providerIds = providers.map(p => p.id);

      // Obtener ratings
      const { data: ratingsData } = await supabase
        .from('ratings')
        .select('service_id, rating')
        .in('service_id', providers.flatMap(p => p.services.map(s => s.id)));

      // Obtener suscripciones (para identificar premium)
      const { data: subscriptions } = await supabase
        .from('provider_subscriptions')
        .select(`
          provider_id,
          status,
          subscription_plans(slug)
        `)
        .in('provider_id', providerIds);

      // Obtener fotos destacadas del portfolio
      const { data: featuredPhotos } = await supabase
        .from('provider_portfolio')
        .select('provider_id, image_url')
        .in('provider_id', providerIds)
        .eq('is_featured', true)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      // Procesar y rankear proveedores
      const processedProviders: (ProviderSearchResult & { score: number })[] = providers.map(provider => {
        const providerServices = provider.services || [];

        // Calcular ratings
        const serviceIds = providerServices.map(s => s.id);
        const providerRatings = ratingsData?.filter(r => serviceIds.includes(r.service_id)) || [];
        const avgRating = providerRatings.length > 0
          ? providerRatings.reduce((sum, r) => sum + r.rating, 0) / providerRatings.length
          : 0;
        const ratingCount = providerRatings.length;

        // Precio mínimo
        const activePrices = providerServices.map(s => s.price);
        const minPrice = activePrices.length > 0 ? Math.min(...activePrices) : 0;

        // Categorías únicas
        const categories = [...new Set(providerServices.map(s => s.category))];

        // Suscripción
        const subscription = subscriptions?.find(s => s.provider_id === provider.id);
        const planSlug = subscription?.subscription_plans?.slug || 'basico';
        const subscriptionStatus = subscription?.status || 'inactive';
        const isPremium = planSlug === 'premium' || planSlug === 'profesional';

        // Foto destacada
        const featuredPhoto = featuredPhotos?.find(f => f.provider_id === provider.id);
        const hasFeaturedPhoto = !!featuredPhoto;

        // SCORE PARA RANKING
        let score = 0;

        // 1. Plan premium/profesional: +100 puntos (PRIORIDAD MÁXIMA)
        // Aceptar tanto 'active' como 'trialing' como estados válidos
        if (isPremium && ['active', 'trialing'].includes(subscriptionStatus)) {
          score += 100;
        }

        // 2. Rating alto: +50 puntos máximo (rating * 10)
        score += avgRating * 10;

        // 3. Tiene fotos destacadas: +30 puntos
        if (hasFeaturedPhoto) {
          score += 30;
        }

        // 4. Número de reviews: +1 punto por review (máximo 20)
        score += Math.min(ratingCount, 20);

        // 5. Tiene dirección completa: +5 puntos
        if (provider.address) {
          score += 5;
        }

        logger.info(`📊 ${provider.business_name}: score=${score}, premium=${isPremium}, rating=${avgRating.toFixed(1)}, photos=${hasFeaturedPhoto}`);

        return {
          id: provider.id,
          business_name: provider.business_name || '',
          bio: provider.bio || '',
          avatar_url: provider.avatar_url || '',
          featured_image: featuredPhoto?.image_url || null,
          city: provider.city || '',
          province: provider.province || '',
          address: provider.address || '',
          phone: '',
          avg_rating: Math.round(avgRating * 10) / 10,
          rating_count: ratingCount,
          min_price: minPrice,
          plan_slug: planSlug,
          subscription_status: subscriptionStatus,
          services: providerServices.map(s => ({
            id: s.id,
            name: s.name,
            category: s.category,
            price: s.price,
          })),
          categories,
          score,
        };
      });

      // Ordenar por score (mayor a menor) y tomar los mejores 6
      const topProviders = processedProviders
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);

      logger.info('🏆 Top 6 providers:', topProviders.map(p => `${p.business_name} (${p.score})`));

      return topProviders;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  if (isLoading) {
    return (
      <section className="py-20 px-4 relative z-10 bg-cream">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/30">
              <Sparkles className="h-3 w-3 mr-1" />
              Recomendados
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              Proveedores Destacados
            </h2>
            <p className="font-body text-[#8C7E6F] max-w-2xl mx-auto">
              Los mejores salones de belleza de tu ciudad
            </p>
          </div>

          {/* Loading skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-neutral-200 rounded-xl h-80"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (featuredProviders.length === 0) {
    return null; // No mostrar la sección si no hay proveedores
  }

  return (
    <section className="py-20 px-4 relative z-10 bg-cream">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <Badge className="bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/30 hover:bg-[#B8860B]/20">
            <Sparkles className="h-3 w-3 mr-1" />
            Recomendados para ti
          </Badge>
          <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
            Proveedores Destacados
          </h2>
          <p className="font-body text-[#8C7E6F] max-w-2xl mx-auto">
            Los mejores profesionales de belleza seleccionados para ti
          </p>
        </div>

        {/* Grid de proveedores */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {featuredProviders.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              isFavorite={false}
            />
          ))}
        </div>

        {/* Ver todos button */}
        <div className="text-center">
          <Link to="/providers">
            <Button size="lg" variant="outline" className="border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-white shadow-sm rounded-xl font-body font-semibold">
              Ver todos los proveedores
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
